import { Link, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, formatCurrency } from "@odingard/design-system";
import { ErrorState, LoadingState } from "@odingard/design-system";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

export function MarketplaceBundle() {
  const { slug = "" } = useParams();
  const state = useAsync(async () => {
    const { bundles } = await api.catalogBundles();
    return bundles.find((b) => b.slug === slug) ?? null;
  }, [slug]);

  const bundle = state.data;
  const icon = bundle?.kind === "addon" ? "🛡️" : "📦";
  const comingSoon = bundle?.lifecycleStatus === "coming_soon";
  const priceEntries = bundle ? Object.entries(bundle.prices) : [];

  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: "var(--ods-space-5)" }}>
          <Link to="/marketplace" style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
            ← Back to Marketplace
          </Link>
        </div>

        {state.loading && <LoadingState label="Loading bundle…" />}
        {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
        {state.data === null && !state.loading && (
          <ErrorState title="Bundle not found" description="This bundle is no longer available." />
        )}

        {bundle && (
          <>
            <div className="ods-row" style={{ gap: "var(--ods-space-4)", alignItems: "flex-start" }}>
              <div className="feature__icon" aria-hidden="true" style={{ fontSize: "2rem" }}>
                {icon}
              </div>
              <div style={{ flex: 1 }}>
                <div className="ods-row" style={{ gap: "var(--ods-space-3)", alignItems: "center" }}>
                  <h1 style={{ margin: 0 }}>{bundle.name}</h1>
                  {comingSoon ? <Badge tone="neutral">Coming soon</Badge> : <Badge tone="success">Available</Badge>}
                </div>
                <p style={{ color: "var(--ods-color-muted)", marginTop: "var(--ods-space-2)" }}>{bundle.description}</p>
                <div style={{ marginTop: "var(--ods-space-4)" }}>
                  {comingSoon ? (
                    <Button disabled variant="ghost">
                      Coming soon
                    </Button>
                  ) : (
                    <Link to="/pricing">
                      <Button>View pricing</Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: "var(--ods-space-7)", display: "grid", gap: "var(--ods-space-5)" }}>
              {bundle.applications.length > 0 && (
                <Card>
                  <CardHeader title="Included applications" />
                  <CardBody>
                    <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
                      {bundle.applications.map((a) => (
                        <Link key={a.slug} to={`/marketplace/apps/${a.slug}`}>
                          <Button variant="secondary" size="sm">
                            {a.lifecycleStatus === "available" ? a.name : `${a.name} (coming soon)`}
                          </Button>
                        </Link>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              )}

              {priceEntries.length > 0 && (
                <Card>
                  <CardHeader title="Pricing" />
                  <CardBody>
                    <div className="ods-row" style={{ gap: "var(--ods-space-6)" }}>
                      {priceEntries.map(([interval, price]) => (
                        <div key={interval}>
                          <div style={{ fontSize: "var(--ods-text-lg)", fontWeight: 600 }}>
                            {formatCurrency(price.unitAmount, price.currency)}
                          </div>
                          <div style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>
                            per {interval === "year" ? "year" : "month"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
